/**
 * tagFilter.js — pure helpers for the CaseList tag-filter sidebar.
 *
 * Kept DOM-free so they can be unit-tested under `node --test` and reused.
 */

/**
 * Build the sorted sidebar tag list from the FULL vocabulary (so ANY tag is
 * selectable, even one not on the loaded page) overlaid with per-tag counts
 * derived from the loaded case set.
 *
 * As a safety net (e.g. the GET /api/tags fetch failed) any tag SEEN on a loaded
 * case but missing from the vocabulary is still surfaced — so the filter degrades
 * to "tags on loaded cases" rather than vanishing.
 *
 * System tags sort first; then alphabetically by label. A tag with no loaded
 * cases has count 0.
 *
 * @param {Array<{slug,label,isSystem,colour}>} vocab  full tag vocabulary
 * @param {Array<{tags?:Array}>}                cases  loaded (role-filtered) cases
 * @returns {Array<{slug,label,isSystem,colour,count}>}
 */
export function tagDisplayLabel(t) {
  return (t?.label || t?.slug || '').trim();
}

export function buildSidebarTags(vocab = [], cases = []) {
  const counts = {};
  const seen = {};
  for (const c of cases) {
    for (const t of (c.tags || [])) {
      if (!t || !t.slug) continue;
      counts[t.slug] = (counts[t.slug] || 0) + 1;
      if (!seen[t.slug]) seen[t.slug] = t;   // remember shape for the orphan fallback
    }
  }

  const map = {};
  for (const t of vocab) {
    if (!t || !t.slug) continue;
    map[t.slug] = {
      slug: t.slug,
      label: tagDisplayLabel(t) || t.slug,
      isSystem: !!t.isSystem,
      colour: t.colour || null,
      count: counts[t.slug] || 0,
    };
  }
  for (const slug of Object.keys(seen)) {
    if (!map[slug]) {
      const t = seen[slug];
      map[slug] = {
        slug,
        label: tagDisplayLabel(t) || slug,
        isSystem: !!t.isSystem,
        colour: t.colour || null,
        count: counts[slug] || 0,
      };
    }
  }

  return Object.values(map).sort(
    (a, b) => (Number(b.isSystem) - Number(a.isSystem))
      || (a.label || '').localeCompare(b.label || '')
  );
}
